import React from "react";
import { cva } from "class-variance-authority";
import {
  type BaseField,
  Checkbox,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  withConditional,
} from "@bleu.builders/ui";
import { cn } from "@perk/core/lib/utils";
import type { FieldValues, UseFormReturn } from "react-hook-form";

export interface CheckboxGroupFieldProps extends BaseField {
  options: Array<{
    label: string;
    tooltip?: string;
    value: string;
  }>;
}

const checkboxGroupVariants = cva("w-full flex flex-col gap-2", {
  variants: {
    layout: {
      stack: "items-stretch",
      inline: "items-stretch",
    },
  },
  defaultVariants: {
    layout: "stack",
  },
});

interface CheckboxGroupWithoutSectionProps {
  field: CheckboxGroupFieldProps;
  form: UseFormReturn<FieldValues>;
}

const CheckboxGroupWithoutSection = ({
  form,
  field,
}: CheckboxGroupWithoutSectionProps) => (
  <FormField
    control={form.control}
    name={field.name}
    rules={field.required ? { required: true } : {}}
    defaultValue={[]}
    render={({ field: formField }) => (
      <FormItem className="space-y-4 w-full">
        <div className="space-y-2">
          <FormLabel className="text-lg font-semibold" tooltip={field.tooltip}>
            {field.label}
          </FormLabel>
          {field.description && (
            <FormDescription>{field.description}</FormDescription>
          )}
        </div>
        <FormControl>
          <div className={cn(checkboxGroupVariants())}>
            {field?.options?.map((option) => (
              <FormItem key={option.value} className="w-full">
                <label className="cursor-pointer block" htmlFor={option.value}>
                  <div
                    className={cn(
                      "w-full p-4 rounded-xl flex items-center gap-3 transition-colors",
                      formField.value?.includes(option.value)
                        ? "bg-gray-200 hover:bg-gray-300"
                        : "bg-gray-100 hover:bg-gray-200"
                    )}
                  >
                    <FormControl>
                      <Checkbox
                        id={option.value}
                        className="rounded-full"
                        checked={formField.value?.includes(option.value)}
                        onCheckedChange={(checked) =>
                          checked
                            ? formField.onChange([
                                ...(formField.value || []),
                                option.value,
                              ])
                            : formField.onChange(
                                formField.value?.filter(
                                  (value: string) => value !== option.value
                                )
                              )
                        }
                      />
                    </FormControl>
                    <span className="font-medium">{option.label}</span>
                  </div>
                </label>
              </FormItem>
            ))}
          </div>
        </FormControl>
        {field.required && (
          <p className="text-sm text-gray-500">
            You must choose at least one answer
          </p>
        )}
        <FormMessage />
      </FormItem>
    )}
  />
);

export const MultiSelectCheckboxField =
  withConditional<CheckboxGroupFieldProps>(({ form, field }) => {
    return <CheckboxGroupWithoutSection {...{ form, field }} />;
  });
